import {
	OrderAction,
	OrderItemLifecycleStepEnum as Step,
	OrderSetupStage,
	type GetOrderQuery,
} from '@codezo/graphql';
import { Badge, Thumb } from '@codezo/quote-detail';
import {
	FlowDisclosure,
	FlowSubSteps,
	OrderFlow,
	OwnerChip,
	type SampleStep,
	type FlowRow,
	type FlowSection,
	type Owner,
} from '@codezo/order-detail';
import { Box } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Button } from '../../../components/ui/button';
import { ConfirmItemsPanel } from './ConfirmItemsPanel';
import { ActionButtons, ItemActions } from './ItemActions';
import { ProductImage, primaryProductImage } from '../../../components/ui/product-image';
import { formatDate } from '../../../lib/utils';
import { asOwner, mergedState, outstandingNote, rowState, stepOf } from './buildFlow';
import { isAdvancing, partitionItemActions, rowForAction } from './itemActionRows';
import { SampleRoundActions } from './SampleRoundActions';
import { ShipmentActions } from './ShipmentActions';
import { CurrencyText } from '../../../components/CurrencyText';

type Order = NonNullable<GetOrderQuery['getOrder']>;
type OrderItem = Order['items'][number];

/**
 * The order flow, as Flow C draws it: a shared setup section, then one section
 * per product running its own track.
 *
 * ── The rows are the design's, the states are the backend's ──
 *
 * "Sample & approval" covers SAMPLING and APPROVAL together — one row over two
 * steps. That is a mapping, not a re-derivation: every dot's state still comes
 * from the lifecycle's own `completed` / `current` / `skipped`, matched by step
 * id, and `mergedState` says how two of them combine.
 *
 * SAMPLE_PAYMENT was meant to be lifted into the shared setup section, as the
 * design has it. Setup then became confirmation and nothing else, and the step
 * was left with no row anywhere — see `productRows`, which now draws it.
 *
 * ACCEPTED is not drawn. It is true the moment the order exists and can never be
 * `current`, so on a five-item order it would be five rows saying nothing — its
 * completion is what the setup section is about.
 *
 * ── Where the design outruns the data ──
 *
 * The production box ("Cut & sew · Nordic Textile Co. · 60%"). Nothing feeds the
 * progress, and the item carries `vetterEntityId` but no vetter NAME, so the box
 * carries what production actually recorded instead — see `rowContext`.
 *
 * Setup-as-a-gate used to be the other entry here, reconstructed from which
 * actions the server still offered and disagreeing with the item lifecycle as a
 * result. `Order.setupStage` carries it now and is read below.
 */
export function OrderFlowSection({
	order,
	onChanged,
	recordShipment,
}: {
	order: Order;
	onChanged: () => void;
	/**
	 * The order's RECORD_SHIPMENT action plus the page's opener - the shipping
	 * row needs it for an item that is not on a box yet. The page owns the modal;
	 * this only asks for it.
	 */
	recordShipment?: {
		label: string;
		blocked?: boolean | null;
		blockedReason?: string | null;
		onOpen: () => void;
	};
}) {
	// The confirm form is the step's content here, not a dismissible prompt as it
	// is on v1 — so "Skip for now" folds it away rather than removing the only way
	// to confirm, and the row offers it back.
	const [confirmFolded, setConfirmFolded] = useState(false);

	const items = order.items ?? [];
	// Read, not reconstructed. SETUP means at least one product is still
	// unconfirmed and the studio is setting the order up; ACTIVE and CLOSED both
	// mean setup is behind us. Orthogonal to the item lifecycle by design — which
	// is exactly why we stopped inferring it from one.
	const inSetup = order.setupStage === OrderSetupStage.Setup;
	const setupDone = !inSetup;

	const confirmAction = (order.availableActions ?? []).find(
		(a) => a.action === OrderAction.ConfirmOrder
	);
	// Setup is confirmation and nothing else now. The payment routes moved to the
	// order-total card, where the figure they are measured against lives — the
	// spine is about work: sampling, production, shipping.
	const setupSection: FlowSection = {
		key: 'setup',
		title: 'Order setup',
		meta: 'shared across all items · done once',
		thumb: (
			<Thumb size={26}>
				<Box size={13} />
			</Thumb>
		),
		badge: setupDone ? (
			<Badge tone="success" dot>
				Complete
			</Badge>
		) : (
			<OwnerChip owner="admin" role="admin" />
		),
		rows: [
			{
				key: 'confirm',
				label: 'Confirm items',
				state: inSetup ? 'active' : 'done',
				meta: inSetup
					? `${items.length - (order.itemsAwaitingConfirmation ?? 0)} of ${items.length} costed`
					: 'all items costed',
				panel:
					inSetup && confirmAction ? (
						confirmFolded ? (
							<div className="mt-2">
								<Button
									variant="outline"
									size="xs"
									onClick={() => setConfirmFolded(false)}
								>
									{confirmAction.label}
								</Button>
							</div>
						) : (
							<ConfirmItemsPanel
								orderId={order.id}
								items={items}
								onConfirmed={onChanged}
								footer={
									<Button
										variant="ghost"
										size="xs"
										className="ml-auto"
										onClick={() => setConfirmFolded(true)}
									>
										Skip for now
									</Button>
								}
							/>
						)
					) : undefined,
			},
		],
	};

	const productSections = items.map<FlowSection>((item) => {
		const delivered = rowState(stepOf(item, Step.Delivered)) === 'done';
		/* Arrived and still owed for.
		 *
		 * `OwnerChip` reads a delivered item as terminal — "a delivered row owes
		 * nobody anything" — which was true while production waited for payment.
		 * It no longer does: goods are made, shipped and received on an unpaid
		 * product, and the chip then puts a green DELIVERED on the most prominent
		 * line of a product someone still owes for. So the payment step is asked
		 * separately, and says so beside it. */
		const unpaid = rowState(stepOf(item, Step.ProductPayment)) === 'outstanding';
		const rows = productRows(item);

		return {
			key: item.id,
			title: item.productName || 'Product',
			// The design's own line: quantity, which kind of sample, and what the
			// line is worth. The last part is why this is a node — the figure goes
			// through the app's currency component rather than being formatted here.
			meta: (
				<>
					{[
						`Qty ${item.quantity.toLocaleString()}`,
						item.sampleChoice ? sampleChoiceLabel(item) : null,
					]
						.filter(Boolean)
						.join(' · ')}
					{item.lineTotal != null && (
						<>
							{' · line total '}
							<CurrencyText amount={item.lineTotal} className="text-text-4" />
						</>
					)}
				</>
			),
			thumb: (
				<Thumb size={26}>
					<ProductImage
						image={primaryProductImage(item.productImages)}
						alt=""
						className="h-full w-full"
						fallback={<Box size={13} />}
					/>
				</Thumb>
			),
			// `waitingOn`, not a setup-stage guess. "Starts after setup" used to sit
			// here while the order was in SETUP — which told the studio a product had
			// not started on exactly the orders where the client was already being
			// asked to pay for its sample.
			// Both facts, not one: it IS delivered and it IS unpaid, and dropping
			// either leaves the row saying something untrue about the other.
			badge: (
				<span className="flex flex-wrap items-center gap-1.5">
					<OwnerChip owner={itemOwner(item)} role="admin" done={delivered} />
					{unpaid && (
						<Badge tone="warn" dot>
							Unpaid
						</Badge>
					)}
				</span>
			),
			// Both records wrap BEFORE the active-row pass, so a finished passage
			// keeps what it left behind and the live one still opens on top of it.
			rows: withActions(
				withFulfilmentRecord(
					withSampleRecord(rows, item, onChanged),
					order,
					item,
					onChanged
				),
				item,
				order,
				onChanged,
				recordShipment
			),
		};
	});

	/* A card-level ownership chip lived here. It was added when the section chips
	 * could go quiet on an order that was plainly waiting on somebody — an
	 * order-level payment route belongs to no single product, and nothing then
	 * carried it. `waitingOn` closed that: a payment step's actor is set on the
	 * item whose step it is, so the sections say it themselves. Kept, the card
	 * repeated whatever the first section already said, one line above it. */

	return (
		<OrderFlow
			sections={[setupSection, ...productSections]}
			meta={
				setupDone
					? 'setup complete — each product runs its own flow'
					: 'setup is shared, then each product runs its own flow'
			}
		/>
	);
}

/* `sampleFee` lived here — the design's "fee DKK 310 · deducted from the order"
 * on the Sample & approval row. Gone from both spines now, for one reason: a
 * sample was being priced in three places at once, and two of them were net
 * while the one the client pays is gross. The summary rail is where a sample's
 * money is stated, on the invoice's own basis. See the row's own note. */

function sampleChoiceLabel(item: OrderItem): string | null {
	const rounds = item.samples?.length ?? 0;
	if (rounds === 0) return 'no sample';
	return rounds > 1 ? `sample · round ${rounds}` : 'sample';
}

/**
 * Who owes this item's next move.
 *
 * One field. `waitingOn` is the actor of the item's current lifecycle step,
 * resolved server-side — null means nothing is pending, not unknown.
 *
 * What this replaces: a walk through the live sample round's actions, then the
 * item's, then the shipments', each with its own rule for which entries were
 * real moves. Three bugs came out of that, all the same shape — "Cancel item",
 * SET_PAYMENT_TREATMENT and EDIT_DETAILS are each permanently available, and
 * each in turn was read as somebody's turn. None of those rules exist now.
 */
function itemOwner(item: OrderItem): Owner {
	return asOwner(item.waitingOn);
}

/**
 * The four steps under "Production & delivery".
 *
 * Dates only. Every move on this side belongs to something other than the item —
 * production is the studio's own transition, shipping belongs to the box — so
 * they are rendered beneath the steps by `withActions`, where a shipment can
 * bring its own carrier, tracking and buttons without being squeezed onto a row.
 */
/**
 * "50 of 100 delivered", when a step is genuinely part-way through.
 *
 * A product that travels in several goes can be half delivered — one shipment
 * arrived, the other is still moving — and the step is then neither done nor
 * untouched. Saying nothing makes a half-delivered product read exactly like one
 * nothing has arrived for.
 *
 * Counted from the shipment LINES, not from the status: a status is one word for
 * a quantity that can be in two places at once. Null means not computable and is
 * NOT zero, so it reads as "no claim" rather than "none yet".
 */
function partProgress(done: number | null | undefined, total: number, verb: string) {
	if (done == null || done <= 0 || done >= total) return null;
	return `${done.toLocaleString()} of ${total.toLocaleString()} ${verb}`;
}

const FULFILMENT: {
	key: Step;
	label: string;
	date: (i: OrderItem) => string | null | undefined;
	/** What has happened so far, while the step is part-done. */
	progress?: (i: OrderItem) => string | null;
}[] = [
	{ key: Step.ProductPayment, label: 'Payment', date: (i) => i.paidAt },
	{ key: Step.Production, label: 'Production', date: (i) => i.productionCompletedAt },
	{
		key: Step.Delivery,
		label: 'Shipping',
		date: (i) => i.shippedAt,
		progress: (i) => partProgress(i.shippedQuantity, i.quantity, 'shipped'),
	},
	{
		key: Step.Delivered,
		label: 'Delivered',
		date: (i) => i.actualDeliveryDate,
		progress: (i) => partProgress(i.deliveredQuantity, i.quantity, 'delivered'),
	},
];

function fulfilmentSteps(
	item: OrderItem,
	panels: Partial<Record<Step, ReactNode>>,
	actions: Partial<Record<Step, ReactNode>>
): SampleStep[] {
	return FULFILMENT.map(({ key, label, date, progress }) => {
		const step = stepOf(item, key);
		const state = rowState(step);
		const at = date(item);
		return {
			key,
			// OUR word, not the server's, and the one place on this page that is
			// true. The server calls this step "Product payment", which is right
			// when it is a row of its own and redundant under a parent already
			// reading "Production & delivery" — the sample side's sub-step says
			// "Payment" for the same reason.
			label,
			state,
			at: state === 'done' && at ? formatDate(at) : undefined,
			// The server's sentence for what is still owed — "This product has not
			// been paid for." The warn ring says a step was overtaken; this says
			// what it was.
			//
			// Failing that, how far through the step is: an open step that is
			// already part-done has something true to say, and the ring alone
			// cannot say it. Never both — the outstanding reason is the more
			// urgent of the two, and a row carries one line.
			note: step?.outstandingReason ?? progress?.(item) ?? undefined,
			action: actions[key],
			panel: panels[key],
		};
	});
}

/** Which of the four the item is standing on, if any. */
/**
 * How many boxes this product travels in, when that is worth saying.
 *
 * The boxes themselves live under the Shipping sub-step, and that panel only
 * opens once the product is standing on it — Flow C keeps one row open at a
 * time, and a step nothing has reached yet is not it. Which left a product
 * spread across two boxes looking exactly like one travelling in a single box,
 * from acceptance right up until it shipped.
 *
 * So the collapsed parent says the count and nothing else. It is a fact about
 * the passage, legible without opening anything, and it does not pretend to be
 * a move.
 *
 * Only above one: "1 box" is the ordinary case and says nothing. Counted off
 * the item's own list, so an order whose legs have not been fanned out per
 * product still answers honestly — that product really is in one box there.
 */
const boxNote = (item: OrderItem): string | undefined => {
	const count = item.shipments?.length ?? 0;
	return count > 1 ? `${count} boxes` : undefined;
};

function liveFulfilmentStep(item: OrderItem): Step | null {
	return FULFILMENT.find((f) => rowState(stepOf(item, f.key)) === 'active')?.key ?? null;
}

/**
 * Keep a FINISHED sample reachable.
 *
 * `withActions` opens the active row and nothing else, which is right for a step
 * that can still be moved. It is wrong for what a step left behind: the moment a
 * client approved a round, its photos, its virtual delivery, the studio's note,
 * the tracking and every earlier round became unreachable on this page — the row
 * turned `done` and its panel went with it. On a delivered order there was no
 * way back to the sample at all, because the item has no active row either.
 *
 * Sampling is the part of an order people return to most: it is the record of
 * what was agreed before anything was made. So it stays, folded away — closed by
 * default, so the spine still shows one open row at rest.
 */
function withSampleRecord(rows: FlowRow[], item: OrderItem, onChanged: () => void): FlowRow[] {
	const i = rows.findIndex((r) => r.key === 'sample');
	// `done` OR `outstanding`. Keyed on `done` alone, a round the item moved past
	// unpaid had no panel and no disclosure: the spine showed a bare ring labelled
	// "Sample & approval" and the whole round — its steps, its files, the unpaid
	// payment step — was unreachable from the page it belongs to.
	const finished = rows[i]?.state === 'done' || rows[i]?.state === 'outstanding';
	if (i < 0 || !finished || (item.samples?.length ?? 0) === 0) return rows;

	return rows.map((row, j) =>
		j === i
			? {
					...row,
					panel: (
						<FlowDisclosure
							label={
								rows[i].state === 'done'
									? 'View the approved sample'
									: 'View this sample round'
							}
						>
							<SampleRoundActions item={item} onChanged={onChanged} />
						</FlowDisclosure>
					),
				}
			: row
	);
}

/**
 * Keep a FINISHED passage reachable — the whole of it, not just its boxes.
 *
 * The same hole `withSampleRecord` fills, on the other half of the flow, and it
 * was the more visible one: `withActions` hangs the shipment panel off the
 * Shipping SUB-STEP, and only while that sub-step is the live one. The moment a
 * box is marked delivered, Shipping turns `done`, the live step moves on — on a
 * finished item there is no active row left at all, and `withActions` returns
 * before it does anything — and the whole panel goes with it: the carrier, the
 * tracking number, the link to follow it, and every action the box still has.
 *
 * Every one of those outlives delivery. `EDIT_DETAILS` in particular is returned
 * by the server on a DELIVERED shipment precisely so a wrong carrier or a wrong
 * arrival date can be corrected AFTER the fact — and the flow was the one place
 * on the page that could not reach it. The deliveries list could, which left two
 * views of one box disagreeing about what could be done to it.
 *
 * Folded, like the sample record: closed by default, so a finished passage still
 * shows one open row at rest, and the spine does not grow a second panel on
 * every delivered product.
 *
 * ── Why the four steps, and not only the shipments ──
 *
 * This used to open onto `ShipmentActions` alone, which answered the question it
 * was written for — where are the boxes — and lost the one around it. A finished
 * product's record is the PASSAGE: when it was paid, when production completed,
 * when it shipped, when it arrived. Those dates live on the four sub-steps, and
 * `withActions` builds them only for the ACTIVE row — so the moment the goods
 * stopped moving they became unreachable, and a delivered item could show its
 * carrier but not the day production finished.
 *
 * So the disclosure holds the same `FlowSubSteps` the live row does, with the
 * boxes nested under Shipping where they belong. No actions are passed: a
 * finished passage has no move to make from a step, and the shipments bring
 * their own — `EDIT_DETAILS` on a delivered box is exactly why this exists.
 */
function withFulfilmentRecord(
	rows: FlowRow[],
	order: Order,
	item: OrderItem,
	onChanged: () => void
): FlowRow[] {
	const i = rows.findIndex((r) => r.key === 'fulfilment');
	if (i < 0) return rows;

	// Only once the goods stop moving. While Shipping is live the panel belongs
	// open on the sub-step, which is `withActions`' job — two panels for one box
	// would be the duplication this is meant to avoid.
	if (liveFulfilmentStep(item) === Step.Delivery) return rows;

	// `done` OR `outstanding`, for the same reason the sample record takes both:
	// an item delivered but unpaid is finished as far as the boxes go, and its
	// record should not be hostage to a payment step.
	//
	// No shipment requirement any more. It was right while this opened onto the
	// boxes and nothing else — with none there was nothing to disclose — but the
	// passage has dates whether or not anything shipped, and a product that
	// finished production and then stalled is precisely the one someone goes
	// looking for.
	const finished = rows[i].state === 'done' || rows[i].state === 'outstanding';
	if (!finished) return rows;

	return rows.map((row, j) =>
		j === i
			? {
					...row,
					panel: (
						<>
							{row.panel}
							<FlowDisclosure label="View production & delivery record">
								<FlowSubSteps
									steps={fulfilmentSteps(
										item,
										{
											[Step.Delivery]: (
												<ShipmentActions
													item={item}
													onChanged={onChanged}
												/>
											),
										},
										{}
									)}
								/>
							</FlowDisclosure>
						</>
					),
				}
			: row
	);
}

/**
 * Expand the ACTIVE row, and only the active row.
 *
 * ── The rule, from the design ──
 *
 * Flow C renders row content behind `{active && …}`. One row per product is
 * open at a time, and it is the one the product is standing on. Two earlier
 * versions of this broke that rule in opposite directions: first every action
 * went under whichever row was current — putting "Start production" beneath
 * Sample & approval — and then actions were scattered onto the rows they
 * advance, which lit up boxes on rows nothing had reached yet and left the
 * active row holding an all-but-empty box.
 *
 * So: the active row shows what can be done AT that step, plus the server's
 * standing notices. Rows ahead of it show nothing, because nothing has reached
 * them.
 *
 * Cancelling is not here at all. It is an escape from the flow rather than a
 * move along it, the design has no cancel in the spine, and it now sits on the
 * product card where v1 keeps it.
 */
function withActions(
	rows: FlowRow[],
	item: OrderItem,
	order: Order,
	onChanged: () => void,
	recordShipment?: {
		label: string;
		blocked?: boolean | null;
		blockedReason?: string | null;
		onOpen: () => void;
	}
): FlowRow[] {
	const { buttons, notices } = partitionItemActions(item);
	const activeIndex = rows.findIndex((r) => r.state === 'active');
	if (activeIndex < 0) return rows;

	const active = rows[activeIndex];
	/**
	 * What the studio can do from the row the item is standing on.
	 *
	 * Two kinds, and the second is new:
	 *
	 * 1. Moves belonging to THIS row, blocked or not — a blocked one renders
	 *    disabled carrying the server's reason, which is information that belongs
	 *    where the item actually is.
	 * 2. Any unblocked move, wherever `ROW_FOR_STATUS` files it.
	 *
	 * The second exists because starting production no longer waits for payment.
	 * "Start production" sets IN_PRODUCTION, so it is filed under the Production
	 * row — which only becomes current once production has started. While the
	 * action always arrived blocked that circularity was invisible; now that the
	 * backend lets it through with a `warningReason`, the one move the studio was
	 * meant to be able to make early had nowhere to render.
	 *
	 * Only UNBLOCKED ones travel. A blocked move shown on a row it has nothing to
	 * do with is the noise this rule was written to prevent — "Start production"
	 * under Sample & approval, greyed, explaining a gate three steps away.
	 */
	const rowActions = buttons.filter(
		(a) => rowForAction(a) === active.key || (!a.blocked && isAdvancing(a))
	);

	// Two rows whose moves are NOT on the item.
	//
	// Sampling lives on the round (`samples[].availableActions`) and shipping
	// lives on the box (`shipments[].availableActions`) - goods are put on a
	// shipment by the ORDER's RECORD_SHIPMENT, and everything after that belongs
	// to the shipment. Reading only the item's list left both rows dead: the one
	// the product spends most of its life on, and the one it finishes on.
	const sampleRow = active.key === 'sample';
	const fulfilmentRow = active.key === 'fulfilment';
	if (!sampleRow && !fulfilmentRow && rowActions.length === 0 && notices.length === 0) {
		return rows;
	}

	const live = liveFulfilmentStep(item);
	// Only while production is the live step. "In production since ..." beside a
	// pay-for-this-product notice would be answering a question nobody asked yet.
	const context = live === Step.Production ? rowContext(Step.Production, item) : null;

	/**
	 * Every control hangs off the sub-step it acts on.
	 *
	 * Rendered after the run instead — which is how this first went in — the pay
	 * box sat below "Delivered", three steps from the step it belongs to and
	 * indented to the parent, so it read as an action on the whole passage.
	 *
	 * Shipping is the one whose content is not a button: a box brings its own
	 * carrier, tracking and moves, and a split product brings several of them.
	 * That is why these are panels and not the step's `action` slot.
	 */
	/**
	 * A move with nothing to pair it with goes ON the step, not under it.
	 *
	 * `ItemActions` reserves a `flex-[1_1_150px]` column for its context sentence
	 * — the carrier beside "Mark as delivered", the reason beside a blocked move.
	 * With neither a context nor a notice that column is still reserved, so the
	 * button is pushed onto a line of its own and floats at the far edge under an
	 * empty strip. "Start production" under Payment is the case: the studio does
	 * not pay, so there is nothing to say beside it.
	 *
	 * The step's own right slot is where the sample stepper puts its moves, and
	 * it is where this belongs too.
	 */
	const bare = !context && notices.length === 0 && rowActions.length > 0;

	const panels: Partial<Record<Step, ReactNode>> = {};
	const actions: Partial<Record<Step, ReactNode>> = {};
	if (live && bare && live !== Step.Delivery) {
		actions[live] = <ActionButtons item={item} actions={rowActions} onChanged={onChanged} />;
	} else if (live === Step.Delivery) {
		panels[Step.Delivery] = (
			<ShipmentActions item={item} recordShipment={recordShipment} onChanged={onChanged} />
		);
	} else if (live) {
		panels[live] = (
			<ItemActions
				item={item}
				actions={rowActions}
				notices={notices}
				context={context}
				onChanged={onChanged}
			/>
		);
	}

	const node = sampleRow ? (
		<SampleRoundActions item={item} onChanged={onChanged} />
	) : (
		<>
			<FlowSubSteps steps={fulfilmentSteps(item, panels, actions)} />
			{/* Nothing to hang them off: the row is open, so the server is offering
			    something, but no sub-step is current — a move out of sequence, or a
			    step the lifecycle has not marked. Under the run is the fallback, not
			    the default. */}
			{!live && (rowActions.length > 0 || notices.length > 0) && (
				<ItemActions
					item={item}
					actions={rowActions}
					notices={notices}
					context={null}
					onChanged={onChanged}
				/>
			)}
		</>
	);

	return rows.map((row, i) =>
		i === activeIndex
			? {
					...row,
					panel: (
						<>
							{row.panel}
							{node}
						</>
					),
				}
			: row
	);
}

/**
 * The facts the design pairs with each row's move.
 *
 * Every ActionBox in Flow C shows the thing being acted on beside the button —
 * the carrier and tracking number next to "Mark as delivered". Without it the
 * button asks the reader to remember what they are confirming.
 *
 * ── Facts only ──
 *
 * An earlier version wrote "Ready to start production" here, which the frontend
 * is in no position to know: the server was simultaneously saying *product
 * payment is required before production can start*, and the box contradicted
 * itself in two adjacent lines. Whether a move is possible is the backend's to
 * state, and it already does, through `blocked` and `blockedReason`. This says
 * only what is recorded — a date, a carrier, a tracking number — and nothing
 * when there is nothing recorded yet.
 *
 * The design also draws a production PERCENTAGE and progress bar. Nothing feeds
 * either, and a hardcoded bar would be a picture of progress rather than a
 * report of it.
 */
function rowContext(key: Step, item: OrderItem): ReactNode {
	/* A 'shipping' branch lived here, reading `item.carrier`, `item.trackingNumber`
	 * and `item.estimatedDeliveryDate`. It was already unreachable — the Shipping
	 * row renders `ShipmentActions`, which never receives this — and it was the
	 * wrong source anyway: item-level tracking answers with the FIRST shipment
	 * only, so a product split across boxes would have shown one leg's carrier as
	 * though it were the item's. `Order.shipments` is where the full picture is,
	 * and that is what the row already reads. */

	// The design's box here reads "Cut & sew - Nordic Textile Co. - 60%" with a
	// progress bar, and we have none of the three: no process name, no progress
	// field, and `vetterEntityId` carries no vetter NAME (backend ask).
	//
	// So: when production started. That WAS a duplicate of the row's own meta,
	// which is why it was pulled - but the meta is gone from active rows now, so
	// this is the only place the fact appears, and it is what the design's box
	// answers in its own way: this has been running a while, since when?
	if (key === Step.Production) {
		return item.productionStartedAt
			? `In production since ${formatDate(item.productionStartedAt)}`
			: null;
	}

	return null;
}

/**
 * The two rows the design draws, mapped onto the lifecycle's steps.
 *
 * Every state comes from the lifecycle as sent. A product used to be forced to
 * all-upcoming while the order was in SETUP, on the reasoning that nothing in it
 * was actionable yet — but `setupStage` is orthogonal to the item lifecycle by
 * design, and a client can owe a sample payment before the studio has finished
 * costing the order. The server says which step is current; this draws it.
 */
function productRows(item: OrderItem): FlowRow[] {
	const sampling = stepOf(item, Step.Sampling);
	const approval = stepOf(item, Step.Approval);
	const production = stepOf(item, Step.Production);
	const delivery = stepOf(item, Step.Delivery);
	const done = stepOf(item, Step.Delivered);

	const payment = stepOf(item, Step.ProductPayment);

	/**
	 * A date on the right, and ONLY on a finished row.
	 *
	 * The design is strict about this and we were not. Its right-hand slot is
	 * `done ? fcDate(…) : …`, so a row that is still running carries nothing
	 * there — what it has to say is in the box below it. We were printing
	 * "started Sep 20" beside an active Production row AND "In production since
	 * Sep 20" inside its box: the same fact, twice, on two adjacent lines.
	 *
	 * Mono, because `fcDate` is. Dates are the one thing on this rail that a
	 * reader scans down a column, and a proportional font makes them ragged.
	 */
	const at = (state: FlowRow['state'], verb: string, value?: string | null): ReactNode =>
		state === 'done' && value ? (
			// The VERB is part of it. `opfbDoneMeta` is {approved, paid, finished,
			// delivered} + the date, all inside one mono span - "paid Sep 23", not
			// a bare "Sep 23", which leaves the reader to work out which of the
			// row's several dates this one is. Stripping the verbs was mine.
			<span className="font-mono">{`${verb} ${formatDate(value)}`}</span>
		) : undefined;

	// ONE row over three steps: paying for the round, making it, approving it.
	//
	// SAMPLE_PAYMENT had a row of its own, added when the step was drawn nowhere.
	// The round's own stepper draws it as "Payment", so the spine was saying it
	// twice — a top-level "Sample payment" directly above a sub-step of the same
	// name. Merged, the row covers the whole of sampling and the stepper inside it
	// carries the detail, which also means the sub-steps are visible while the
	// round is still being paid for rather than appearing only afterwards.
	const sampleState = mergedState([stepOf(item, Step.SamplePayment), sampling, approval]);
	const deliveredState = rowState(done);
	const fulfilmentState = mergedState([payment, production, delivery, done]);

	const approvedAt = item.samples?.find((s) => s.approvedAt)?.approvedAt;

	return [
		{
			key: 'sample',
			label: 'Sample & approval',
			state: sampleState,
			// A date once the round is approved, and nothing before it.
			//
			// The design gives this row the sample's fee and treatment, and it came
			// out for the reason it came out of the client's spine: it priced one
			// sample a third time. The summary rail already states the charge (gross,
			// the figure on the invoice) against a Samples line reading zero, and a
			// third number here — net, per item — made the page look like it
			// disagreed with itself about what a sample costs.
			//
			// The treatment has not gone anywhere. It is set from the sample's own
			// steps below, where SET_PAYMENT_TREATMENT lives, and named on the
			// transaction once money moves.
			meta:
				sampleState === 'done'
					? at(sampleState, 'approved', approvedAt)
					: // What sampling never settled, once the item has moved past it.
						// The row is otherwise a warn ring over a label, which says
						// something is wrong without saying what.
						outstandingNote(sampleState, [
							stepOf(item, Step.SamplePayment),
							sampling,
							approval,
						]),
		},
		// ONE row over the four steps that get the goods made and delivered.
		//
		// They were four top-level rows, which is how Sample & approval started
		// too: a passage drawn as unrelated stages. Nesting them puts the product
		// side in the same grammar as the sample side — a parent that says where
		// the passage has got to, and its own steps inside carrying the dates and
		// the moves.
		//
		// The parent merges all four, so it is `done` only once the goods have
		// arrived and `skipped` only if the whole passage never applied.
		{
			key: 'fulfilment',
			label: 'Production & delivery',
			state: fulfilmentState,
			// What is still OWED comes before what was achieved.
			//
			// `at` keys on the Delivered sub-step, not on this row: the goods can
			// have arrived while the product is still unpaid, and production no
			// longer waits for payment. Asked first, it returned "delivered Sep 24"
			// — a finished-looking date beside a warn ring, with the reason for the
			// ring dropped entirely. The row then said something was wrong without
			// saying what, which on an admin's screen is the one thing it had to say.
			//
			// `outstandingNote` is undefined unless the row is `outstanding`, so this
			// only takes precedence in the case it exists for; the row tones it warn.
			meta:
				outstandingNote(fulfilmentState, [payment, production, delivery, done]) ??
				at(deliveredState, 'delivered', item.actualDeliveryDate) ??
				boxNote(item),
		},
	];
}
